import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DynamicDialogRef } from '@shared/components/ui/dynamic-dialog/dynamic-dialog-ref';
import { DynamicDialogConfig } from '@shared/components/ui/dynamic-dialog/dynamic-dialog.component';

/** Data contract passed to {@link MfaStepUpDialogComponent} via the dialog config. */
export interface MfaStepUpDialogData {
  /** Heading shown above the code field (e.g. "Admin security verification"). */
  title: string;
  /** Supporting copy below the heading. */
  message: string;
  /**
   * RF-QA-196 · Optional description of WHAT the verification protects
   * (e.g. "Symbol mapping data"); rendered as "Verifying access to {context}."
   * between the title and the message. Omit for the generic prompt.
   */
  context?: string;
}

/** RF-QA-196 · Element ids the dialog chrome references for aria-labelledby / aria-describedby. */
export const MFA_STEP_UP_TITLE_ID = 'mfaStepUpTitle';
export const MFA_STEP_UP_CONTEXT_ID = 'mfaStepUpContext';
export const MFA_STEP_UP_MESSAGE_ID = 'mfaStepUpMessage';

/**
 * MFA step-up code dialog.
 *
 * Visually aligned with the login page's inline Two-Factor Authentication step
 * (centered, mono, tracking-widest code field + a primary "Verify" action) so
 * the admin re-verification prompt reads as the same product surface rather
 * than a generic input dialog. Closes with the trimmed code on confirm, or
 * `null` on cancel — format validation stays in MfaStepUpService, the single
 * source of truth for what counts as a valid TOTP / backup code.
 */
@Component({
  selector: 'app-mfa-step-up-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './mfa-step-up-dialog.component.html',
})
export class MfaStepUpDialogComponent {
  readonly data: MfaStepUpDialogData;
  readonly code = signal('');

  /**
   * The code field. The native `autofocus` attribute does not reliably fire in
   * a dynamically-instantiated dialog, so we focus it explicitly after the
   * first render (mirrors the login challenge's #mfaCodeInput focus).
   */
  private readonly codeInput = viewChild<ElementRef<HTMLInputElement>>('codeInput');

  constructor(
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {
    this.data = config.data as MfaStepUpDialogData;
    afterNextRender(() => this.codeInput()?.nativeElement.focus());
  }

  onConfirm(): void {
    const trimmed = this.code().trim();
    if (trimmed.length === 0) {
      return;
    }
    this.dialogRef.close(trimmed);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
